import React, {useEffect,useMemo,useState} from "react";
import {api} from "../api";
import {NovelStorySceneRichEditor} from "./NovelStorySceneManuscript";
import NextScenesConfirmDialog from "./NextScenesConfirmDialog";

const ID=/^[a-f0-9]{24}$/i;
const SCENE_WINDOW_SIZE=6;
const clean=v=>String(v??"").trim();
const sceneEdit=s=>({id:s.id,title:s.title||"",text:s.text||"",
  manuscriptContent:s.manuscriptContent??null});
// This is a small revision adapter around the EXISTING Novel rich scene editor.
// It NEVER loads /scenes/:id PATCH or the ordinary Story save APIs.
export default function NovelRevisionDraftWorkspaceB2({
  storyId,revisionId,isFR=false,onBack,onDirtyChange,
}){
  const label=(en,fr)=>isFR?fr:en;
  const [record,setRecord]=useState(null);
  const [selectedId,setSelectedId]=useState("");
  const [selectedChapterId,setSelectedChapterId]=useState("");
  const [pendingNavigation,setPendingNavigation]=useState(null);
  const [editing,setEditing]=useState(null);
  const [dirty,setDirty]=useState(false);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  useEffect(()=>{onDirtyChange?.(dirty)},[dirty,onDirtyChange]);
  useEffect(()=>{
    let active=true;
    setRecord(null);setSelectedId("");setSelectedChapterId("");setPendingNavigation(null);
    setEditing(null);setDirty(false);setError("");setMessage("");
    if(!ID.test(storyId)||!ID.test(revisionId))return()=>{active=false};
    setBusy("load");
    api.get(`/api/stories/${encodeURIComponent(storyId)}/production-revisions-v1/${encodeURIComponent(revisionId)}`)
      .then(result=>{
        if(!active)return;
        if(result?.ok!==true||result.revisionId!==revisionId||
           result.storyId!==storyId||!Array.isArray(result.draftContent?.chapters)||
           !Array.isArray(result.draftContent?.scenes)||result.state!=="active_draft")
          throw new Error("Private revision draft could not be verified.");
        const first=result.draftContent.chapters.flatMap(ch=>ch.sceneIds||[])[0]||"";
        const chapter=result.draftContent.chapters.find(ch=>(ch.sceneIds||[]).includes(first));
        setRecord(result);setSelectedId(first);
        setSelectedChapterId(chapter?.id||result.draftContent.chapters[0]?.id||"");
        const found=result.draftContent.scenes.find(s=>s.id===first);
        setEditing(found?sceneEdit(found):null);
      }).catch(e=>{if(active)setError(clean(e?.message)||"Could not open private revision.")})
      .finally(()=>{if(active)setBusy("")});
    return()=>{active=false};
  },[storyId,revisionId]);
  const chapters=useMemo(()=>record?.draftContent?.chapters||[],[record]);
  const scenes=useMemo(()=>record?.draftContent?.scenes||[],[record]);
  const selectedChapter=useMemo(()=>chapters.find(ch=>ch.id===selectedChapterId)||null,
    [chapters,selectedChapterId]);
  // As in Book Workspace, show a bounded range rather than every Scene at once.
  // The window follows the selected Scene, so there is no additional navigation state.
  const chapterSceneIds=selectedChapter?.sceneIds||[];
  const selectedSceneIndex=chapterSceneIds.indexOf(selectedId);
  const sceneWindowStart=Math.floor(Math.max(0,selectedSceneIndex)/SCENE_WINDOW_SIZE)*SCENE_WINDOW_SIZE;
  const sceneWindowEnd=Math.min(chapterSceneIds.length,sceneWindowStart+SCENE_WINDOW_SIZE);
  const visibleSceneIds=chapterSceneIds.slice(sceneWindowStart,sceneWindowEnd);
  function applySelection(chapterId,sceneId){
    const found=scenes.find(s=>s.id===sceneId);
    setSelectedChapterId(chapterId);setSelectedId(found?sceneId:"");
    setEditing(found?sceneEdit(found):null);
    setDirty(false);setMessage("");setError("");
  }
  function changeScene(id){
    const chapter=chapters.find(ch=>(ch.sceneIds||[]).includes(id));
    if(!chapter||!scenes.some(s=>s.id===id)||id===selectedId)return;
    if(dirty){setPendingNavigation({kind:"scene",chapterId:chapter.id,sceneId:id});return;}
    applySelection(chapter.id,id);
  }
  function changeChapter(id){
    const chapter=chapters.find(ch=>ch.id===id);
    if(!chapter||id===selectedChapterId)return;
    const first=(chapter.sceneIds||[]).find(sceneId=>scenes.some(s=>s.id===sceneId))||"";
    if(dirty){setPendingNavigation({kind:"chapter",chapterId:id,sceneId:first});return;}
    applySelection(id,first);
  }
  function confirmNavigation(){
    const pending=pendingNavigation;
    setPendingNavigation(null);
    if(!pending)return;
    if(pending.kind==="back"){onBack?.();return;}
    const chapter=chapters.find(ch=>ch.id===pending.chapterId);
    if(!chapter||(pending.sceneId&&
      (!(chapter.sceneIds||[]).includes(pending.sceneId)||!scenes.some(s=>s.id===pending.sceneId))))return;
    applySelection(pending.chapterId,pending.sceneId||"");
  }
  async function save(){
    if(busy||!record||!editing||!dirty)return;
    setBusy("save");setError("");setMessage("");
    try{
      const next=await api.patch(
        `/api/stories/${encodeURIComponent(storyId)}/production-revisions-v1/${encodeURIComponent(revisionId)}/scenes/${encodeURIComponent(editing.id)}`,
        {expectedDraftVersion:record.draftVersion,
          title:editing.title,text:editing.text,
          manuscriptContent:editing.manuscriptContent}
      );
      if(next?.ok!==true||next.revisionId!==revisionId||
         next.storyId!==storyId||next.state!=="active_draft"||
         next.draftVersion!==record.draftVersion+1||
         next.sourceSnapshotId!==record.sourceSnapshotId||
         next.sourceInteriorArtifactId!==record.sourceInteriorArtifactId||
         next.sourceCoverArtifactId!==record.sourceCoverArtifactId)
        throw new Error("Private revision save could not be confirmed.");
      setRecord(next);
      const saved=next.draftContent.scenes.find(s=>s.id===editing.id);
      if(!saved)throw new Error("Saved Scene is missing from the private draft.");
      setEditing(sceneEdit(saved));setDirty(false);
      setMessage(label("Revision scene saved privately.","Scène de révision enregistrée en privé."));
    }catch(e){
      setError(e?.status===409?
        label("This revision changed elsewhere. Copy your edits before reloading; they were not overwritten.",
          "Cette révision a changé ailleurs. Copiez vos modifications avant de recharger.") :
        clean(e?.message)||label("Revision save failed.","L'enregistrement a échoué."));
    }finally{setBusy("")}
  }
  function back(){
    if(dirty){setPendingNavigation({kind:"back"});return;}
    onBack?.();
  }
  return <section data-novel-revision-workspace-b2="owner-private"
    style={{border:"1px solid #a8bddc",borderLeft:"5px solid #4f74aa",
      background:"#f6f9ff",padding:18,borderRadius:14,display:"grid",gap:14}}>
    <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
      <div>
        <div style={{fontSize:12,color:"#4f668b",fontWeight:800,letterSpacing:1}}>
          {label("PRIVATE REVISION · NOT APPROVED","RÉVISION PRIVÉE · NON APPROUVÉE")}
        </div>
        <h4 style={{margin:"5px 0",fontSize:20}}>
          {record?.draftContent?.title||label("Working Revision","Révision en cours")}
        </h4>
        <p style={{fontSize:13,color:"#475569",margin:0}}>
          {label("The original approved Canon and PDF pair remain unchanged.",
            "Le Canon d'origine et les PDF approuvés restent inchangés.")}
        </p>
      </div>
      <button type="button" className="ns-button ns-button-outline"
        onClick={back} disabled={Boolean(busy)}>{label("Back to Versions","Retour aux versions")}</button>
    </div>
    {busy==="load"?<p role="status">{label("Loading private draft…","Chargement du brouillon…")}</p>:null}
    {error?<p role="alert" style={{color:"#991b1b"}}>{error}</p>:null}
    {message?<p role="status" style={{color:"#166534"}}>{message}</p>:null}
    {record?<>
      <div style={{fontSize:12,color:"#64748b"}}>
        {label("Draft revision","Version du brouillon")} {record.draftVersion}
        {" · "}{label("Source archive","Archive source")}: {clean(record.sourceSnapshotId).slice(0,8)}…
      </div>
      <div data-novel-revision-chapter-navigator="true"
        style={{padding:14,border:"1px solid #d2dce9",borderRadius:12,
          background:"#fff",display:"grid",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <strong>{label("Chapter Navigator","Navigateur de chapitres")}</strong>
          <span style={{fontSize:13,color:"#64748b"}}>
            {chapters.length} {label("chapters","chapitres")} · {scenes.length} {label("scenes","scènes")}
          </span>
        </div>
        <label style={{display:"grid",gap:5,fontWeight:650,maxWidth:520}}>
          {label("Jump to Chapter","Aller au chapitre")}
          <select className="ns-input" aria-label={label("Jump to Chapter","Aller au chapitre")}
            disabled={Boolean(busy)} value={selectedChapterId}
            onChange={e=>changeChapter(e.target.value)}>
            {chapters.length===0?<option value="">{label("No chapters","Aucun chapitre")}</option>:null}
            {chapters.map((ch,index)=><option key={ch.id} value={ch.id}>
              {index+1}. {ch.title||label("Chapter","Chapitre")+" "+(index+1)}
            </option>)}
          </select>
        </label>
        {selectedChapter?<div data-novel-revision-active-chapter={selectedChapter.id}
          style={{display:"grid",gap:8}}>
          <strong>{label("Scenes in","Scènes de")} {selectedChapter.title}</strong>
          <div data-novel-revision-scene-pager="true"
            style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <button type="button" className="ns-button ns-button-outline"
              aria-label={label("Previous scenes","Scènes précédentes")}
              onClick={()=>changeScene(chapterSceneIds[sceneWindowStart-SCENE_WINDOW_SIZE])}
              disabled={Boolean(busy)||sceneWindowStart===0}>
              {label("‹ Previous","‹ Précédentes")}
            </button>
            <div data-novel-revision-chapter-scenes="true"
              aria-label={label("Scenes in selected chapter","Scènes du chapitre sélectionné")}
              style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
              {visibleSceneIds.map((id,k)=><button type="button" key={id}
                onClick={()=>changeScene(id)}
                aria-pressed={selectedId===id} disabled={Boolean(busy)}
                className={selectedId===id?"ns-button ns-button-primary":"ns-button ns-button-outline"}>
                {label("Scene","Scène")} {sceneWindowStart+k+1}
              </button>)}
            </div>
            <button type="button" className="ns-button ns-button-outline"
              aria-label={label("Next scenes","Scènes suivantes")}
              onClick={()=>changeScene(chapterSceneIds[sceneWindowEnd])}
              disabled={Boolean(busy)||sceneWindowEnd>=chapterSceneIds.length}>
              {label("Next ›","Suivantes ›")}
            </button>
            {chapterSceneIds.length?<span data-novel-revision-scene-range="true"
              style={{fontSize:13,color:"#64748b",whiteSpace:"nowrap"}}>
              {label("Scenes","Scènes")} {sceneWindowStart+1}–{sceneWindowEnd} {label("of","sur")} {chapterSceneIds.length}
            </span>:null}
          </div>
          {!(selectedChapter.sceneIds||[]).length?
            <span style={{fontSize:13,color:"#64748b"}}>
              {label("No scenes in this chapter.","Aucune scène dans ce chapitre.")}
            </span>:null}
        </div>:null}
      </div>
      {editing?<div style={{display:"grid",gap:10}}>
        <label style={{display:"grid",gap:6,fontWeight:650}}>
          {label("Scene title","Titre de la scène")}
          <input className="ns-input" maxLength={200} disabled={Boolean(busy)}
            value={editing.title} onChange={e=>{
              setEditing(x=>({...x,title:e.target.value}));setDirty(true);setMessage("");
            }}/>
        </label>
        <NovelStorySceneRichEditor key={revisionId+selectedId}
          id={selectedId} title={editing.title} text={editing.text}
          manuscriptContent={editing.manuscriptContent}
          disabled={Boolean(busy)} lang={isFR?"fr":"en"}
          onChange={value=>{
            setEditing(x=>({...x,text:String(value?.text||""),
              manuscriptContent:value?.manuscriptContent??null}));
            setDirty(true);setMessage("");
          }}/>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          {dirty?<span style={{fontSize:13,color:"#92400e"}}>
            {label("Unsaved revision changes","Modifications non enregistrées")}</span>:null}
          <button className="ns-button ns-button-primary" type="button"
            data-novel-revision-save-scene="true"
            onClick={save} disabled={!dirty||Boolean(busy)}>
            {busy==="save"?label("Saving…","Enregistrement…"):
              label("Save Revision Scene","Enregistrer la scène révisée")}
          </button>
        </div>
      </div>:null}
      <p style={{fontSize:12,color:"#64748b",margin:0}}>
        {label("This first revision checkpoint edits Scenes only. Front/Back Matter, structure and new approval are not enabled yet.",
          "Cette première étape modifie uniquement les scènes. Les autres éléments et l'approbation restent désactivés.")}
      </p>
    </>:null}
    <NextScenesConfirmDialog
      open={Boolean(pendingNavigation)}
      title={label("Unsaved revision changes","Modifications de révision non enregistrées")}
      message={pendingNavigation?.kind==="back"
        ? label("This scene contains unsaved changes. Return to Versions without saving them?",
            "Cette scène contient des modifications non enregistrées. Revenir aux versions sans les enregistrer ?")
        : label("This scene contains unsaved changes. Discard them before switching chapters or scenes?",
            "Cette scène contient des modifications non enregistrées. Les abandonner avant de changer de chapitre ou de scène ?")}
      stayLabel={label("Keep Editing","Continuer à modifier")}
      leaveLabel={label("Discard Changes","Abandonner les modifications")}
      onStay={()=>setPendingNavigation(null)}
      onLeave={confirmNavigation}
    />
  </section>;
}
